import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Step,
  StepButton,
  Stepper,
  TextField,
} from "@mui/material";
import * as React from "react";
import BatteryTypeSelect from "../../../components/device/selects/battery_type_select";
import MeterTypeSelect from "../../../components/device/selects/meter_type_select";
import DeviceClassSelect from "../../../components/device/selects/device_class_select";
import MPPTTrackingSelect from "../../../components/device/selects/mppt_tracking_select";
import BatteryTrackingSelect from "../../../components/device/selects/battery_tracking_select";
import MeteringModeSelect from "../../../components/device/selects/metering_mode_select";
import MeteringDirectionSelect from "../../../components/device/selects/metering_direction_select";
import USBPortSelect, { pathToIDMap } from "../../../components/device/selects/usb_port_select";
import { useSnackbar } from "notistack";
import { getUsedPorts, processValue } from "../../../scripts/utils";
import HelpedSelect from "../../../components/general/helped_select";
import { Protocol } from "../../../model/device";
import { useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { fetchSiteData } from "../../../scripts/api_v2/site_client";
import { useParams } from "react-router-dom";
import useBackend from "../../../hooks/use_backend";

const meterTemplate = {
  device_type: "Gavazzi",
  device_id: "",
  name: "",
  sampling_time: 1,
  enabled: true,
  address: "",
  protocol: "RS485",
  device_class: "meter",
  mode: 2,
  direction: 1,
};
const batteryTemplate = {
  device_type: "Victron",
  device_id: "",
  name: "",
  sampling_time: 1,
  enabled: true,
  address: "",
  protocol: "TCP",
  port: 502,
  device_class: "battery",
  capacity: 0,
  charge_rate: 0,
  mppt_tracking_mode: 0,
  battery_tracking_mode: 0,
};
const devTemplate = { device_type: "Victron", name: "", device_class: "battery" };

const steps = ["Select device type", "Select protocol", "Finalize settings"];

export function AddDeviceStepper({ open, setOpen, handleAddDevice }) {
  const params = useParams();
  const backend = useBackend();
  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });
  const [device, setDevice] = React.useState(devTemplate);
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState({});
  const { enqueueSnackbar } = useSnackbar();
  const totalSteps = () => {
    return steps.length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (device.device_class === "battery") {
        setDevice({ ...batteryTemplate, device_type: device.device_type, name: device.name });
      } else if (device.device_class === "meter") {
        setDevice({ ...meterTemplate, device_type: device.device_type, name: device.name });
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => {
    setActiveStep(step);
  };

  function handleClose() {
    setOpen(false);
    setActiveStep(0);
    setDevice(devTemplate);
  }

  function renderTypeSelect() {
    if (device.device_class === "battery") {
      return (
        <BatteryTypeSelect
          value={device.device_type}
          onChange={(e) => {
            handleDevChange("device_type", e.target.value, true);
          }}
        />
      );
    } else if (device.device_class === "meter") {
      return (
        <MeterTypeSelect
          value={device.device_type}
          onChange={(e) => {
            handleDevChange("device_type", e.target.value, true);
          }}
        />
      );
    }
  }

  function handleClassChange(value) {
    if (value === "battery") setDevice(batteryTemplate);
    else if (value === "meter") setDevice(meterTemplate);
  }

  function handleProtocolChange(value) {
    setDevice((prev) => {
      return { ...prev, address: "" };
    });
    handleDevChange("protocol", value, true);
  }

  function handleDevChange(field, value, isString = false) {
    const processed = processValue(value, isString);

    setDevice((prev) => {
      const updated = { ...prev };
      updated[field] = processed;
      return updated;
    });
  }

  function validateDevice(device) {
    if (device.name === "") {
      return { type: "error", message: "Please fill in a name!", returnStep: 0 };
    } else if (device.protocol === "TCP") {
      if (device.device_id === "") {
        return { type: "error", message: "Please fill in a MAC address!", returnStep: 1 };
      } else if (device.address === "") {
        return { type: "error", message: "Please fill in an IP address!", returnStep: 1 };
      } else if (device.port === "") {
        return { type: "error", message: "Please fill in a port!", returnStep: 1 };
      }
    } else if (device.protocol === "RS485") {
      if (device.address === "") {
        return { type: "error", message: "Please select an USB port!", returnStep: 1 };
      }
    }
    return { type: "success", message: "Device added!" };
  }

  async function handleAdd() {
    const dev = { ...device };
    if (device.protocol === "RS485") {
      dev.device_id = device.device_type.toUpperCase() + "_" + pathToIDMap[device.address];
    }
    const validation = validateDevice(dev);
    if (validation.type === "success") {
      try {
        const res = await handleAddDevice(dev);
        setDevice(devTemplate);
        enqueueSnackbar(validation.message, { variant: "success" });
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Device add failed", { variant: "error" });
      }
    } else {
      setActiveStep(validation.returnStep); // go to the step with wrongfilled data
      enqueueSnackbar(validation.message, { variant: "error" });
    }
  }

  function renderLabel(index, label) {
    if (activeStep <= index) {
      return label;
    } else if (index === 0) {
      return device.device_class.toUpperCase() + ": " + device.device_type;
    } else if (index === 1) {
      return device.protocol;
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      fullWidth
      maxWidth={"md"}
      classes={{ paper: { minHeight: "80vh", maxHeight: "80vh" } }}
    >
      <DialogTitle id="add-dialog-title">Add device</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label} completed={completed[index]}>
              <StepButton
                color="inherit"
                onClick={() => {
                  handleStep(index);
                }}
              >
                {renderLabel(index, label)}
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <Grid mt={3} container justifyContent="start">
          {activeStep === 0 && (
            <>
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <DeviceClassSelect value={device.device_class} onChange={(e) => handleClassChange(e.target.value)} />
              </Grid>
              <Grid md={6} xs={12} item />

              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                {renderTypeSelect(device.device_class)}
              </Grid>
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <TextField
                  label={"Device name"}
                  fullWidth
                  value={device.name}
                  onChange={(e) => handleDevChange("name", e.target.value, true)}
                />
              </Grid>
            </>
          )}
          {activeStep === 1 && (
            <>
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <HelpedSelect
                  options={Protocol}
                  label={"Protocol"}
                  key="protocol"
                  info="Select the communication protocol between elion box and device"
                  selected={device.protocol}
                  onChange={(e) => {
                    handleProtocolChange(e.target.value);
                  }}
                />
              </Grid>
              {device.protocol === "TCP" && (
                <Grid
                  md={6}
                  xs={12}
                  item
                  width={"80%"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  px={2}
                  py={2}
                >
                  <TextField
                    label={"MAC address"}
                    fullWidth
                    value={device.device_id}
                    onChange={(e) => handleDevChange("device_id", e.target.value, true)}
                  />
                </Grid>
              )}
              {device.protocol === "TCP" ? (
                <Grid
                  md={6}
                  xs={12}
                  item
                  width={"80%"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  px={2}
                  py={2}
                >
                  <TextField
                    label={(device.protocol === "TCP" ? "IP" : "Device") + " address"}
                    fullWidth
                    value={device.address}
                    onChange={(e) => handleDevChange("address", e.target.value, true)}
                  />
                </Grid>
              ) : (
                <Grid
                  md={6}
                  xs={12}
                  item
                  width={"80%"}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                  px={2}
                  py={2}
                >
                  <USBPortSelect
                    value={device.address}
                    onChange={(e) => {
                      handleDevChange("address", e.target.value, true);
                    }}
                    disabledPorts={getUsedPorts(siteData.discovered)}
                  />
                </Grid>
              )}
              {device.protocol !== "TCP" && (
                <Grid
                  md={6}
                  xs={12}
                  item
                  width={"80%"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  px={2}
                  py={2}
                >
                  <TextField
                    label={"Port"}
                    fullWidth
                    value={device.port}
                    onChange={(e) => handleDevChange("port", e.target.value, false)}
                  />
                </Grid>
              )}
            </>
          )}
          {activeStep === 2 && (
            <>
              {device.device_class === "battery" && (
                <>
                  <Grid
                    md={6}
                    xs={12}
                    item
                    width={"80%"}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                    px={2}
                    py={2}
                  >
                    <BatteryTrackingSelect
                      batteryType={device.device_type}
                      value={device.battery_tracking_mode}
                      onChange={(e) => handleDevChange("battery_tracking_mode", e.target.value, false)}
                    />
                  </Grid>
                  <Grid
                    md={6}
                    xs={12}
                    item
                    width={"80%"}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                    px={2}
                    py={2}
                  >
                    <MPPTTrackingSelect
                      value={device.mppt_tracking_mode}
                      onChange={(e) => handleDevChange("mppt_tracking_mode", e.target.value, false)}
                    />
                  </Grid>
                </>
              )}
              {device.device_class === "meter" && (
                <>
                  <Grid
                    md={6}
                    xs={12}
                    item
                    width={"80%"}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                    px={2}
                    py={2}
                  >
                    <MeteringModeSelect
                      value={device.mode}
                      onChange={(e) => handleDevChange("mode", e.target.value, false)}
                    />
                  </Grid>
                  <Grid
                    md={6}
                    xs={12}
                    item
                    width={"80%"}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                    px={2}
                    py={2}
                  >
                    <MeteringDirectionSelect
                      value={device.direction}
                      onChange={(e) => handleDevChange("direction", e.target.value, false)}
                    />
                  </Grid>
                </>
              )}
              <Grid
                md={6}
                xs={12}
                item
                width={"80%"}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                px={2}
                py={2}
              >
                <TextField
                  label={"Sampling time"}
                  fullWidth
                  value={device.sampling_time}
                  onChange={(e) => handleDevChange("sampling_time", e.target.value, false)}
                />
              </Grid>
              <Grid md={6} xs={12} item width={"80%"} />
            </>
          )}
        </Grid>
        <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
          {activeStep === 0 ? (
            <Button onClick={handleClose} sx={{ mr: 1, color: "red" }}>
              Cancel
            </Button>
          ) : (
            <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          <Box sx={{ flex: "1 1 auto" }} />
          {!isLastStep() ? (
            <Button onClick={handleNext} sx={{ mr: 1 }}>
              Next
            </Button>
          ) : (
            <Button
              disabled={!allStepsCompleted()}
              onClick={() => {
                handleAdd();
              }}
            >
              Add device
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
